"use client";

import { c as _c } from "react-compiler-runtime";
import { useGrowthBook } from "@growthbook/growthbook-react";
import { createContext, useContext, useSyncExternalStore } from "react";
import type { ExperimentKey } from "./experiments";

/** What a visitor who is not (yet) in an experiment arm is served. */
const CONTROL_VARIANT = "control";
const ExperimentHoldoutContext = createContext(false);

/**
 * With `value` true, every experiment under it serves control without being evaluated, so nobody
 * there is assigned an arm or counted. For sessions that are not experiment subjects, such as a
 * sales demo run of the assessment.
 */
export const ExperimentHoldout = ExperimentHoldoutContext.Provider;
export type ExperimentState = {
  /** The assigned variant, or `"control"` until the experiment can be evaluated. */
  readonly variant: string;
  readonly isReady: boolean;
};
const subscribeToNothing = (): (() => void) => () => undefined;
const isClientSnapshot = (): boolean => true;
const isServerSnapshot = (): boolean => false;

/**
 * Evaluates a GrowthBook experiment for the current visitor. Nothing is recorded here: when the
 * experiment rule assigns, GrowthBook publishes the exposure as `Experiment Viewed`, and the bus
 * sends it once analytics is granted.
 *
 * Not evaluated before hydration: the server renders without the visitor id, so an arm chosen
 * while hydrating would not match the server's markup. Callers whose arms look different gate the
 * surface on `isReady`, so control is not flashed at treatment visitors. Waiting for hydration
 * also means the bus is live, so no exposure is published before it mounts. Under an
 * {@link ExperimentHoldout} it serves control and never evaluates.
 */
export function useExperiment(experimentKey) {
  const $ = _c(9);
  const growthbook = useGrowthBook();
  const isHydrated = useSyncExternalStore(subscribeToNothing, isClientSnapshot, isServerSnapshot);
  const isHeldOut = useContext(ExperimentHoldoutContext);
  if (isHeldOut) {
    let t0;
    if ($[0] !== isHydrated) {
      t0 = {
        variant: CONTROL_VARIANT,
        isReady: isHydrated
      };
      $[0] = isHydrated;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  const isReady = isHydrated && growthbook.ready;
  let t0;
  if ($[2] !== experimentKey || $[3] !== growthbook || $[4] !== isReady) {
    t0 = isReady ? growthbook.getFeatureValue(experimentKey, CONTROL_VARIANT) : CONTROL_VARIANT;
    $[2] = experimentKey;
    $[3] = growthbook;
    $[4] = isReady;
    $[5] = t0;
  } else {
    t0 = $[5];
  }
  let t1;
  if ($[6] !== isReady || $[7] !== t0) {
    t1 = {
      variant: t0,
      isReady
    };
    $[6] = isReady;
    $[7] = t0;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  return t1;
}